import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toolsAPI } from '../lib/api';

const TOOL_KEY = 'athlete-importer';
const FIELD_CLASS = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

export default function AthleteImporterPage() {
  const [tool, setTool] = useState(null);
  const [serviceAccountEmail, setServiceAccountEmail] = useState(null);

  const [eventUrl, setEventUrl] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  function loadTool() {
    toolsAPI.list()
      .then(res => {
        const t = (res.data.tools || []).find(x => x.key === TOOL_KEY);
        setTool(t || null);
        setServiceAccountEmail(res.data.service_account_email || null);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load tool'));
  }

  useEffect(() => { loadTool(); }, []);

  const canRun = !running && eventUrl.trim() && sheetUrl.trim();

  async function handleRun() {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await toolsAPI.runAthleteImporter({
        event_url: eventUrl.trim(),
        sheet_url: sheetUrl.trim(),
      });
      const data = res.data || {};
      if (res.status >= 400) {
        setError(data.error || `Run failed (${res.status})`);
      } else {
        setResult(data);
      }
      loadTool();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <Link to="/tools" className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1 mb-4">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tools
      </Link>

      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">{tool?.name || 'LiveHeats Athlete Importer'}</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          {tool?.description || 'Fetch event athletes from LiveHeats and merge new ones into a Google Sheet'}
        </p>
        {tool?.last_run_at && (
          <p className="text-xs text-gray-600 mt-1">
            Last run: {new Date(tool.last_run_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Service-account share notice */}
      <div className="mb-5 px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded text-xs text-gray-400 leading-relaxed">
        The destination Google Sheet must be shared with{' '}
        {serviceAccountEmail ? (
          <code className="text-gray-200">{serviceAccountEmail}</code>
        ) : (
          <span className="text-amber-500">the TNL Tools service account</span>
        )}{' '}
        as Editor, and the destination tab must have a header row with a column named{' '}
        <code className="text-gray-200">id</code>. New athletes are appended below the existing rows; athletes whose{' '}
        <code className="text-gray-200">id</code> is already present are skipped.
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
      )}

      <div className="mb-5 space-y-3">
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">LiveHeats event URL or ID</label>
          <input
            placeholder="https://liveheats.com/events/304340  (or just 304340)"
            value={eventUrl}
            onChange={e => setEventUrl(e.target.value)}
            disabled={running}
            className={FIELD_CLASS}
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Google Sheet tab URL</label>
          <input
            placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=..."
            value={sheetUrl}
            onChange={e => setSheetUrl(e.target.value)}
            disabled={running}
            className={FIELD_CLASS}
          />
          <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">
            Open the destination tab in Sheets and copy the URL — it'll end with <code className="text-gray-500">#gid=...</code>, which tells the tool exactly which tab to append to.
          </p>
        </div>
      </div>

      <div className="mb-6">
        <button
          onClick={handleRun}
          disabled={!canRun}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
        >
          {running ? 'Importing…' : 'Run import'}
        </button>
      </div>

      {/* Run result */}
      {result && (
        <div className="mb-5 bg-[#111111] border border-[#2a2a2a] rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="px-2 py-0.5 rounded text-xs font-semibold uppercase bg-green-900/30 text-green-400">
              {result.status}
            </div>
            <div className="text-sm text-white">
              {result.added_count} added
            </div>
            <div className="text-sm text-gray-500">
              {result.skipped_count} already present · {result.fetched_count} fetched
            </div>
            {result.tab_name && (
              <div className="text-xs text-gray-600">
                → <span className="text-gray-400">{result.tab_name}</span>
                {result.event_name && <span className="text-gray-600"> from “{result.event_name}”</span>}
              </div>
            )}
          </div>

          {result.added_count === 0 ? (
            <p className="text-sm text-gray-500">No new athletes — everyone in this event was already in the sheet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#2a2a2a] text-gray-500">
                  <th className="text-left py-2 font-medium">ID</th>
                  <th className="text-left py-2 font-medium">Name</th>
                  <th className="text-left py-2 font-medium">Nationality</th>
                </tr>
              </thead>
              <tbody>
                {(result.added || []).map((a) => (
                  <tr key={a.id} className="border-b border-[#1a1a1a] last:border-0">
                    <td className="py-2 text-gray-400 font-mono">{a.id}</td>
                    <td className="py-2 text-white">{a.name}</td>
                    <td className="py-2 text-gray-400">{a.nationality}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
            <a href={result.sheet_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400 text-xs">
              Open sheet ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
